import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { changePassword, validatePassword } from '../../services/authService';
import { toast } from 'react-toastify';

const DEBOUNCE_DELAY = 500;

const ChangePassword = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordState, setPasswordState] = useState({ isValid: false, error: '' });
    const { user, isAuthenticated } = useSelector((state) => state.auth);

    const validationState = useRef({
        timer: null,
        userData: useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), [])
    });

    useEffect(() => {
        if (!isAuthenticated || !user) {
            navigate('/login');
        }
    }, [isAuthenticated, user, navigate]);

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
    const newPassword = watch('newPassword');
    
    const updatePasswordState = useCallback((isValid, errorMessage = '') => {
        setPasswordState({ isValid, error: errorMessage });
    }, []);

    const validatePasswordOnChange = useCallback(async (password) => {
        if (!password || password.length === 0) {
            updatePasswordState(false, '');
            return;
        }

        try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData?.username || '';
            const phoneNumber = userData?.phoneNumber || '';
            const result = await validatePassword(password, username, phoneNumber);
            if (result.responseCode === 1) {
                updatePasswordState(true, '');
            } else {
                updatePasswordState(false, result.responseMessage || 'Invalid password format');
            }
        } catch (err) {
            toast.error(err.response?.data?.responseMessage || 'Could not validate password');
            updatePasswordState(false, err.response?.data?.responseMessage || 'Could not validate password');
        }
    }, [updatePasswordState]);

    useEffect(() => {
        const { timer } = validationState.current;
        clearTimeout(timer);

        validationState.current.timer = setTimeout(() => {
            validatePasswordOnChange(newPassword);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(validationState.current.timer);
    }, [newPassword, validatePasswordOnChange]);

    const onSubmit = async (data) => {
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!token) {
            setError('No authentication token found. Please log in again.');
            navigate('/login');
            return;
        }
    
        if (!userData || !userData.userId) {
            console.error('Missing user data:', { userData });
            setError('User information not available. Please log in again.');
            navigate('/login');
            return;
        }
        if (!passwordState.isValid) {
            setError('Please correct the password format issues before submitting.');
            return;
        }        
        try {
            setLoading(true);
            setError('');
            
            const changePasswordData = {
                oldPassword: data.oldPassword,
                password: data.newPassword, 
                confirmPassword: data.confirmPassword
            };
       
            const response = await changePassword(changePasswordData);
            
            if (response.responseCode === 1) {
                setSuccess('Password changed successfully');
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                setError(response.responseMessage || 'Failed to change password');
            }
        } catch (err) {
            console.error('Change password error:', err);
            if (err.message.includes('User not found')) {
                setError('User account not found. Please verify your login or contact support.');
            } else if (err.message.includes('Invalid old password')) {
                setError('Your current password is incorrect. Please try again.');
            } else if (err.message.includes('Password must be')) {
                setError(err.message); 
            } else {
                setError(err.message || 'An error occurred while changing password');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default',
            }}
        >
            <Paper elevation={1} sx={{ p: 4, width: '100%', maxWidth: '400px', }}>
                <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                    Change Password
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        type="password"
                        label="Current Password"
                        {...register('oldPassword', {
                            required: 'Current password is required'
                        })}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword?.message}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        type="password"
                        label="New Password"
                        {...register('newPassword', {
                            required: 'New password is required',
                            minLength: {
                                value: 8,
                                message: 'Password must be at least 8 characters'
                            }
                        })}
                        error={!!errors.newPassword || !!passwordState.error}
                        helperText={errors.newPassword?.message}
                    />
                    
                    {passwordState.error && (
                        <FormHelperText error={true} sx={{ ml: 1 }}>
                            {passwordState.error}
                        </FormHelperText>
                    )}
                    
                    {passwordState.isValid && newPassword && (
                        <FormHelperText sx={{ color: 'success.main', ml: 1 }}>
                            Password meets all requirements
                        </FormHelperText>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        type="password"
                        label="Confirm New Password"
                        {...register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: value => 
                                value === newPassword || 'Passwords do not match'
                        })}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3 }}
                        disabled={loading || !passwordState.isValid || !newPassword}
                    >
                        {loading ? (
                            <CircularProgress size={24} />
                        ) : (
                            'Change Password'
                        )}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to Dashboard
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default ChangePassword;